import { useEffect } from 'react';
import { getFiles as getFilesForAdmin, uploadFile as uploadFileFromAdmin, bulkUploadFiles } from '../Services/admin/mediabank';
import {
	getFiles as getFilesForUser,
	MediabankGetFilesParams,
	unlinkFileFromObject,
	updateNameAndDescription,
	uploadFile,
	linkExternalFile,
	getFrontEndObjectLinks,
} from '../Services/mediabank';
import { MediabankFileSettings, MediabankSearchParams, MediabankSettings, MediabankUploadLocation } from '../Types/mediabank';
import { useBizMutation, useBizQuery } from './useBizQuery';
import { useQueryClient } from '@tanstack/react-query';
import { ObjectType } from '../Types/base';
import { PortalSettingKey } from '../Types/portal';

const QUERYKEY_MEDIABANK_FILES_ADMIN = 'MEDIABANK_FILES_ADMIN';
export const QUERYKEY_MEDIABANK_FILES_USER = 'MEDIABANK_FILES_USER';
export const QUERYKEY_MEDIABANK_FRONTENDOBJECTLINKS = 'MEDIABANK_FRONTENDOBJECTLINKS';

export const useMediabankGetFilesForAdmin = (params: MediabankSearchParams) => {
	const wrapperFn = () => {
		return getFilesForAdmin(params);
	};

	const { data } = useBizQuery(
		[QUERYKEY_MEDIABANK_FILES_ADMIN, params.top, params.dateFrom, params.dateTo, params.searchTerm],
		wrapperFn
	);
	return data;
};

export const useMediabankGetFilesForUser = (params?: MediabankGetFilesParams) => {
	return useMediabankGetFilesForUserQuery(params).data;
};

export const useMediabankGetFilesForUserQuery = (params?: MediabankGetFilesParams) => {
	const wrapperFn = async () => {
		if (params) {
			return await getFilesForUser(params);
		}
	};

	return useBizQuery(
		[
			QUERYKEY_MEDIABANK_FILES_USER,
			params?.FileTypes,
			params?.GetDummyContentIfDbEmpty,
			params?.GetRelatedFiles,
			params?.IncludeInvisible,
			params?.IncludePrivate,
			params?.IncludeViews,
			params?.JourneyOrder,
			params?.LoadCommonLocals,
			params?.MapLikes,
			params?.ObjectIds,
			params?.ObjectTypes,
			params?.TagIds,
			params?.UseComments,
			params?.FillSourceUrls,
		],
		wrapperFn,
		params !== undefined
	);
};

export const useMediabankGetFrontEndObjectLinks = (urlFileFilter: string, enabled: boolean) => {
	const wrapperFn = async () => {
		return await getFrontEndObjectLinks(urlFileFilter);
	};

	const { data: mediabankLinks } = useBizQuery([QUERYKEY_MEDIABANK_FRONTENDOBJECTLINKS, urlFileFilter], wrapperFn, enabled);
	return mediabankLinks;
};

export const useMediabankMutations = () => {
	const queryClient = useQueryClient();
	const wrapperFn = (params: {
		file: File;
		name: string;
		description: string;
		isPrivate: boolean;
		isInvisible: boolean;
		uploadLocation: MediabankUploadLocation;
		settings?: MediabankSettings;
		fromAdmin?: boolean;
	}) => {
		if (params.fromAdmin) {
			//i think the admin backend is only used for uploading files from the admin mediabank list, maybe we should make its own hook for that later
			return uploadFileFromAdmin(
				params.file,
				params.name,
				params.description,
				params.isPrivate,
				params.isInvisible,
				params.uploadLocation,
				params.settings
			);
		} else {
			return uploadFile(
				params.file,
				params.name,
				params.description,
				params.isPrivate,
				params.isInvisible,
				params.uploadLocation,
				params.settings
			);
		}
	};

	const unlinkFileFromObjectWrapperFn = (params: { fileId: number; objectType: ObjectType; objectId: number }) => {
		return unlinkFileFromObject(params.fileId, params.objectType, params.objectId);
	};

	const updateNameAndDescriptionWrapperFn = (params: { fileId: number; name: string; description: string }) => {
		return updateNameAndDescription(params.fileId, params.name, params.description);
	};

	const linkFileWrapperFn = (params: {
		link: string;
		fileType: number;
		thumbnail: string;
		uploadedFrom: MediabankUploadLocation;
		isPrivate: boolean;
		isInvisible: boolean;
		name: string;
		description: string;
		settings: MediabankFileSettings;
		urlRewrite: string;
		uploadSettings?: MediabankSettings;
	}) => {
		return linkExternalFile(
			params.link,
			params.fileType,
			params.thumbnail,
			params.uploadedFrom,
			params.isPrivate,
			params.isInvisible,
			params.name,
			params.description,
			params.settings,
			params.urlRewrite,
			params.uploadSettings
		);
	};

	const {
		mutate: mutateUpload,
		mutateAsync: mutateUploadAsync,
		statusState: statusStateUploadFile,
		data: uploadFileResult,
		error: uploadFileError,
	} = useBizMutation(wrapperFn);
	const {
		mutate: mutateUnlink,
		mutateAsync: mutateUnlinkAsync,
		statusState: statusStateUnlinkFromObject,
	} = useBizMutation(unlinkFileFromObjectWrapperFn);
	const { mutate: mutateUpdateNameAndDescription, statusState: statusStateUpdateNameAndDescription } = useBizMutation(
		updateNameAndDescriptionWrapperFn
	);
	const { mutate: mutateLinkFile } = useBizMutation(linkFileWrapperFn, {
		getQueryKeyForInvalidation: () => [[QUERYKEY_MEDIABANK_FILES_USER]],
	});

	useEffect(() => {
		if (statusStateUploadFile === 'SUCCESS' && uploadFileResult) {
			queryClient.invalidateQueries({ queryKey: [QUERYKEY_MEDIABANK_FILES_USER] });
		}
	}, [statusStateUploadFile, uploadFileResult]);

	useEffect(() => {
		if (statusStateUnlinkFromObject === 'SUCCESS') {
			queryClient.invalidateQueries({ queryKey: [QUERYKEY_MEDIABANK_FILES_USER] });
		}
	}, [statusStateUnlinkFromObject]);

	useEffect(() => {
		if (statusStateUpdateNameAndDescription === 'SUCCESS') {
			queryClient.invalidateQueries({ queryKey: [QUERYKEY_MEDIABANK_FILES_USER] });
		}
	}, [statusStateUpdateNameAndDescription]);

	return {
		uploadFile: (
			file: File,
			name: string,
			description: string,
			isPrivate: boolean,
			isInvisible: boolean,
			uploadLocation: MediabankUploadLocation,
			settings?: MediabankSettings,
			fromAdmin?: boolean
		) => mutateUpload({ file, name, description, isPrivate, isInvisible, uploadLocation, settings, fromAdmin }),
		uploadFileAsync: (
			file: File,
			name: string,
			description: string,
			isPrivate: boolean,
			isInvisible: boolean,
			uploadLocation: MediabankUploadLocation,
			settings?: MediabankSettings,
			fromAdmin?: boolean
		) => mutateUploadAsync({ file, name, description, isPrivate, isInvisible, uploadLocation, settings, fromAdmin }),
		uploadFileResult,
		statusStateUploadFile,
		uploadFileError,
		unlinkFileFromObject: (fileId: number, objectType: ObjectType, objectId: number) =>
			mutateUnlink({ fileId, objectType, objectId }),
		unlinkFileFromObjectAsync: (fileId: number, objectType: ObjectType, objectId: number) =>
			mutateUnlinkAsync({ fileId, objectType, objectId }),
		statusStateUnlinkFromObject,
		updateNameAndDescription: (fileId: number, name: string, description: string) =>
			mutateUpdateNameAndDescription({ fileId, name, description }),
		statusStateUpdateNameAndDescription,
		linkExternalFile: (
			link: string,
			fileType: number,
			thumbnail: string,
			uploadedFrom: MediabankUploadLocation,
			isPrivate: boolean,
			isInvisible: boolean,
			name: string,
			description: string,
			settings: MediabankFileSettings,
			urlRewrite: string,
			uploadSettings?: MediabankSettings
		) =>
			mutateLinkFile({
				link,
				fileType,
				thumbnail,
				uploadedFrom,
				isPrivate,
				isInvisible,
				name,
				description,
				settings,
				urlRewrite,
				uploadSettings,
			}),
	};
};

export const useMediabankBulkUpload = () => {
	const queryClient = useQueryClient();

	const wrapperFn = (params: { files: { file: File; portalSettingKeys: PortalSettingKey[] }[]; folderId?: number | null }) => {
		return bulkUploadFiles(params.files, params.folderId);
	};

	const { mutate, statusState, data, error } = useBizMutation(wrapperFn, {
		getQueryKeyForInvalidation: () => [[QUERYKEY_MEDIABANK_FILES_ADMIN]],
	});

	useEffect(() => {
		if (statusState === 'SUCCESS') {
			queryClient.invalidateQueries({ queryKey: [QUERYKEY_MEDIABANK_FILES_ADMIN] });
		}
	}, [statusState, queryClient]);

	return {
		bulkUpload: (files: { file: File; portalSettingKeys: PortalSettingKey[] }[], folderId?: number | null) => mutate({ files, folderId }),
		result: data,
		statusState,
		error,
	};
};
